import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  LinearProgress,
  Alert,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload,
  Cancel,
  CheckCircle,
  InsertDriveFile,
  Image,
  PictureAsPdf,
  Delete,
  OpenInNew
} from '@mui/icons-material';
import { supabase } from '../../config/supabase';
import { useFileContext } from '../../contexts/FileContext';

const UPLOAD_PANEL_BG = 'linear-gradient(165deg, rgba(10, 44, 98, 0.34), rgba(7, 32, 74, 0.3))';
const UPLOAD_PANEL_BORDER = 'none';
const POPUP_RADIUS = '8px';

const FileUploadDialog = ({ open, onClose, onUploadSuccess }) => {
  const { uploadedFiles, removeUploadedFile, loading, emiratesId } = useFileContext();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Supported file types
  const supportedTypes = {
    pdf: '.pdf',
    images: ['.jpg', '.jpeg', '.png', '.bmp', '.tiff', '.gif']
  };

  const acceptedTypes = [...supportedTypes.images, supportedTypes.pdf].join(',');

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (![...supportedTypes.images, supportedTypes.pdf].includes(fileExtension)) {
        setError('Please select a valid file type (PDF, JPG, JPEG, PNG, BMP, TIFF, or GIF)');
        return;
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setError(null);
      
      // Automatically upload the file
      try {
        await uploadFileToSupabase(file);
        // Automatically close dialog after successful upload
        setTimeout(() => {
          handleClose();
        }, 1500);
      } catch (err) {
        console.error('Upload error:', err);
      }
    }
  };

  const uploadFileToSupabase = async (file) => {
    try {
      setUploading(true);
      setUploadStatus('Uploading file...');
      
      if (!emiratesId) {
        throw new Error('User Emirates ID not found. Please log in again.');
      }
      
      // Generate unique filename with timestamp
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      
      // Upload to user-specific folder: {emiratesId}/filename
      const filePath = `${emiratesId}/${fileName}`;
      console.log(`Uploading file to: ${filePath} for user ${emiratesId}`);
      
      // Upload to Supabase storage bucket
      const { error } = await supabase.storage
        .from('AI-doctor')
        .upload(filePath, file);

      if (error) {
        throw error;
      }

      // Get public URL using the full path
      const { data: publicData } = supabase.storage
        .from('AI-doctor')
        .getPublicUrl(filePath);

      setUploadProgress(100);
      setUploadStatus('Upload successful!');
      
      // Call success callback with file path
      onUploadSuccess({
        fileName: file.name,
        filePath: publicData.publicUrl,
        supabasePath: filePath, // Store full path with user folder
        fileType: file.type,
        fileSize: file.size
      });

      return publicData.publicUrl;
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
      throw err;
    } finally {
      setUploading(false);
    }
  };


  const handleClose = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStatus('');
    setError(null);
    setUploading(false);
    onClose();
  };

  const triggerFileSelect = () => {
    fileInputRef.current.click();
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) {
      return <PictureAsPdf sx={{ color: '#9ecfff' }} />;
    } else if (fileType.includes('image')) {
      return <Image sx={{ color: '#9ecfff' }} />;
    }
    return <InsertDriveFile sx={{ color: '#9ecfff' }} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteFile = async (fileId, supabasePath) => {
    try {
      console.log('🗑️ Attempting to delete file:', { fileId, supabasePath });
      
      // Delete from Supabase storage
      const { error } = await supabase.storage
        .from('AI-doctor')
        .remove([supabasePath]);

      if (error) {
        console.error('❌ Error deleting file from Supabase:', error);
        setError(`Failed to delete file: ${error.message}`);
        return;
      }

      console.log('✅ Supabase deletion successful');
      
      // Remove from local state
      removeUploadedFile(fileId);
      console.log('✅ File deleted successfully from UI');
    } catch (err) {
      console.error('❌ Delete error:', err);
      setError('Failed to delete file');
    }
  };

  const handleOpenFile = (filePath) => {
    window.open(filePath, '_blank');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(160deg, rgba(6, 32, 84, 0.38), rgba(4, 24, 66, 0.34))',
          backdropFilter: 'blur(20px)',
          border: 'none',
          boxShadow: '0 18px 44px rgba(2, 14, 38, 0.38), 0 0 18px rgba(38, 135, 230, 0.14)',
          borderRadius: POPUP_RADIUS,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogContent
        sx={{
          p: { xs: 2, sm: 2.5 },
          pt: { xs: 2.5, sm: 3 },
          maxHeight: '70vh',
          overflow: 'auto',
          background: 'linear-gradient(180deg, rgba(8, 33, 76, 0.26), rgba(4, 22, 58, 0.24))',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <CloudUpload sx={{ color: '#d9e9f7' }} />
            <Typography sx={{ color: '#d9e9f7', fontWeight: 600, fontFamily: 'Dubai', fontSize: { xs: '14pt', sm: '16pt' } }}>
              Upload File
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: '#d9e9f7',
              border: 'none',
              borderRadius: POPUP_RADIUS,
              background: 'rgba(8, 38, 84, 0.35)',
              '&:hover': { background: 'rgba(61, 143, 227, 0.18)' },
            }}
          >
            <Cancel />
          </IconButton>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Upload Section */}
        <Box sx={{ mb: 3 ,mt:3}}>
          {!selectedFile ? (
            <Paper
              elevation={0}
              sx={{
                py: 2.4,
                px: 3,
                textAlign: 'center',
                border: 'none',
                borderRadius: POPUP_RADIUS,
                background: UPLOAD_PANEL_BG,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(165deg, rgba(14, 52, 108, 0.5), rgba(8, 36, 82, 0.46))',
                  boxShadow: '0 10px 22px rgba(3, 20, 54, 0.22)',
                }
              }}
              onClick={triggerFileSelect}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2
              }}>
                <Box sx={{
                  background: 'linear-gradient(145deg, rgba(14, 66, 138, 0.95), rgba(42, 160, 238, 0.82))',
                  borderRadius: POPUP_RADIUS,
                  p: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 6px 14px rgba(7, 30, 76, 0.18)',
                }}>
                  <CloudUpload sx={{ 
                    fontSize: 32, 
                    color: 'white'
                  }} />
                </Box>
                
                <Typography 
                  variant="h6"
                  sx={{
                    background: 'linear-gradient(90deg, #f2fbff, #74dcff)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 700,
                  }}
                >
                  Upload a file
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Box>
              <Paper sx={{ p: 2, background: UPLOAD_PANEL_BG, border: UPLOAD_PANEL_BORDER, borderRadius: POPUP_RADIUS }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircle sx={{ color: '#c9d6e1', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ color:"#c9d6e1",fontWeight: 500 ,fontSize:"14pt",fontFamily:"Dubai"}}>
                    {selectedFile.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ color:"white",fontWeight: 400 ,fontSize:"12pt",fontFamily:"Dubai"}}>
                  Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ color:"#c9d6e1",fontWeight: 500 ,fontSize:"14pt",fontFamily:"Dubai"}}>
                  Type: {selectedFile.type || 'Unknown'}
                </Typography>
              </Paper>

              {uploading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {uploadStatus}
                  </Typography>
                </Box>
              )}

              {uploadStatus === 'Upload successful!' && (
                <Alert severity="success" sx={{
    mt: 2,
    background: "linear-gradient(165deg, rgba(76, 154, 224, 0.16), rgba(28, 88, 154, 0.14))",
    border: "none",
    color: "#c8ecff",
    fontSize: "14pt",
    fontFamily: "Dubai",
    height:"max-content",
    borderRadius: POPUP_RADIUS,
    "& .MuiAlert-icon": {
      color: "#9fdfff",
    },
  }}>
                  File uploaded successfully!
                </Alert>
              )}
            </Box>
          )}
        </Box>

        {error && (
         <Alert
  severity="error"
  sx={{
    mt: 2,
    background: "linear-gradient(165deg, rgba(180, 84, 94, 0.18), rgba(130, 44, 56, 0.14))",
    border: "none",
    color: "#ffd4dc",
    fontSize: "14pt",
    fontFamily: "Dubai",
    height:"max-content",
    borderRadius: POPUP_RADIUS,
    "& .MuiAlert-icon": {
      color: "#ffb3c1",
    },
  }}
>
  {error}
</Alert>
        )}

        {/* Previously Uploaded Files */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, py: 4 }}>
            <CircularProgress sx={{ color: '#002f70' }} />
            <Typography sx={{ ml: 2, color: 'text.secondary',   fontSize: "14pt",
    fontFamily: "Dubai", }}>Loading files...</Typography>
          </Box>
        ) : uploadedFiles.length > 0 ? (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2.5 }}>
              <Chip 
                label={`${uploadedFiles.length} File${uploadedFiles.length > 1 ? 's' : ''} Uploaded`}
                size="medium"
                sx={{ 
                  background: 'linear-gradient(120deg, rgba(58, 162, 238, 0.95), rgba(28, 108, 182, 0.92))',
                  color: '#f3fbff',
                  fontWeight: 700,
                  px: 2,
                  fontSize:"12pt",
                  fontFamily:"Dubai",
                  border: 'none',
                }}
              />
            </Divider>

            <List sx={{ 
              maxHeight: '300px', 
              overflow: 'auto',
              border: UPLOAD_PANEL_BORDER,
              borderRadius: POPUP_RADIUS,
              background: UPLOAD_PANEL_BG,
              p: 0,
              boxShadow: '0 8px 18px rgba(4, 20, 55, 0.16)'
            }}>
              {uploadedFiles.slice().reverse().map((file, index) => (
                <React.Fragment key={file.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(26, 80, 148, 0.32)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      {getFileIcon(file.fileType)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontFamily:"Dubai",fontSize:"14pt",fontWeight:400 }}>
                          {file.fileName}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" display="block" color="text.secondary" component="span" sx={{fontFamily:"Dubai",fontSize:"12pt",fontWeight:400}}>
                          {formatFileSize(file.fileSize)} • {formatDate(file.uploadedAt)}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenFile(file.filePath)}
                        sx={{ 
                          color: '#32affc',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 229, 255, 0.1)',
                          }
                        }}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteFile(file.id, file.supabasePath)}
                        sx={{ 
                          color: '#ff8f8a',
                          '&:hover': {
                            backgroundColor: '#ff908a26',
                          }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default FileUploadDialog;
